import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { API_BASE_URL } from '../../constants';
import { getExtensionConfig, listExtensionTokens, mintExtensionToken, revokeExtensionToken, type ExtensionToken } from '../../apis';

interface Props { open: boolean; onClose: () => void }

const ACK_TIMEOUT_MS = 3000;

/**
 * Connect this browser (#195): the signed-in person mints a token for the
 * Voyager extension. When the extension is installed, it answers the page's
 * probe with its id and the token goes to it over Chrome's extension-only
 * channel (`chrome.runtime.sendMessage(id, …)`) — never a window message,
 * which any script on this origin could read; when it is not installed, the
 * token is shown once to paste into the extension's popup. The token is
 * never shown again, and any browser holding one is listed here to be
 * disconnected.
 */
export default function ExtensionConnect({ open, onClose }: Props) {
  const [present, setPresent] = useState(false);
  const [extensionId, setExtensionId] = useState<string | null>(null);
  // the ids this deployment trusts; a presence message from any other extension is not Voyager
  const trusted = useRef<Set<string> | null>(null);
  const pendingIds = useRef<string[]>([]);
  const [tokens, setTokens] = useState<ExtensionToken[]>([]);
  const [name, setName] = useState(() => defaultName());
  const [minting, setMinting] = useState(false);
  const [shown, setShown] = useState<string | null>(null);
  const [connected, setConnected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ackTimer = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    try { setTokens(await listExtensionTokens()); } catch { /* the list is a convenience */ }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    refresh();
    const accept = (id: string) => {
      if (!trusted.current) { pendingIds.current.push(id); return; }        // the allowlist is not here yet: judged when it is
      if (!trusted.current.has(id)) return;                                   // another extension on this origin is not Voyager
      setPresent(true);
      setExtensionId(id);
    };
    let cancelled = false;
    getExtensionConfig().then((cfg) => {
      if (cancelled) return;
      trusted.current = new Set(cfg.extension_ids || []);
      pendingIds.current.splice(0).forEach(accept);
    }).catch(() => { if (!cancelled) { trusted.current = new Set(); pendingIds.current = []; } });   // unknown allowlist: no one-click handoff
    const onMessage = (e: MessageEvent) => {
      // this window, this origin — a frame's or another origin's message is not the extension's
      if (e.source !== window || e.origin !== window.location.origin || !e.data) return;
      if (e.data.type === 'voyager:extension-present' && typeof e.data.extensionId === 'string' && /^[a-p]{32}$/.test(e.data.extensionId)) accept(e.data.extensionId);
    };
    window.addEventListener('message', onMessage);
    // a handshake, not a one-shot: the extension may have announced itself before this listener was up,
    // so the page probes — a few times, for a content script still loading — and the extension answers
    const probe = () => window.postMessage({ type: 'voyager:extension-probe' }, window.location.origin);
    probe();
    const probes = [400, 1200, 2500].map((ms) => window.setTimeout(probe, ms));
    return () => { cancelled = true; window.removeEventListener('message', onMessage); probes.forEach((t) => window.clearTimeout(t)); if (ackTimer.current) window.clearTimeout(ackTimer.current); };
  }, [open, refresh]);

  const connect = async () => {
    setMinting(true); setError(null); setConnected(null); setShown(null);
    try {
      const minted = await mintExtensionToken(name);
      const runtime = (window as any).chrome?.runtime;
      if (present && extensionId && runtime?.sendMessage) {
        // over Chrome's extension-only channel: a page-world script cannot listen to it, unlike a window message
        let answered = false;
        ackTimer.current = window.setTimeout(() => { if (!answered) setError('The extension did not answer — paste the token into its popup instead.'); if (!answered) setShown(minted.token); }, ACK_TIMEOUT_MS);
        runtime.sendMessage(extensionId, { type: 'voyager:extension-token', token: minted.token, apiBase: API_BASE_URL || window.location.origin }, (answer: any) => {
          answered = true;
          if (ackTimer.current) window.clearTimeout(ackTimer.current);
          if (answer?.ok) { setConnected(answer.me?.display_name || answer.me?.email || 'you'); setShown(null); } else { setError(answer?.error || runtime.lastError?.message || 'the extension did not accept the token'); setShown(minted.token); }
          refresh();
        });
      } else {
        setShown(minted.token);
      }
      refresh();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not mint a token');
    } finally {
      setMinting(false);
    }
  };

  const revoke = async (id: string) => {
    try { await revokeExtensionToken(id); refresh(); } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Could not disconnect'); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect the Stroma extension</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          The extension grounds numbers where you read them — a Tableau view, an email — against the warehouse, as you. It holds a token minted here, never your session; disconnect it below any time.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <TextField size="small" label="This browser" value={name} onChange={(e) => setName(e.target.value.slice(0, 80))} sx={{ flex: 1 }} inputProps={{ 'data-testid': 'ext-name' }} />
          <Button variant="contained" onClick={connect} disabled={minting || !name.trim()} data-testid="ext-connect">{minting ? 'Connecting…' : present ? 'Connect this browser' : 'Mint a token'}</Button>
        </Box>
        <Typography variant="caption" color="text.secondary" data-testid="ext-presence">
          {present ? 'The Stroma extension is installed in this browser.' : 'The Stroma extension is not detected here — install it, or mint a token and paste it into the extension’s popup under Advanced.'}
        </Typography>
        {connected && <Typography sx={{ mt: 1.5, color: 'success.main', fontWeight: 600 }} data-testid="ext-connected">Connected as {connected}. You can close this.</Typography>}
        {shown && (
          <Box sx={{ mt: 1.5, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }} data-testid="ext-token">
            <Typography variant="caption" color="text.secondary">Your token — shown once. Paste it into the extension’s popup (Advanced → Paste a token).</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', flex: 1 }}>{shown}</Typography>
              <Button size="small" onClick={() => navigator.clipboard?.writeText(shown).catch(() => undefined)}>Copy</Button>
            </Box>
          </Box>
        )}
        {error && <Typography sx={{ mt: 1, color: 'error.main' }} variant="body2" data-testid="ext-error">{error}</Typography>}
        {tokens.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Browsers holding a token</Typography>
            {tokens.map((t) => (
              <Box key={t.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }} data-testid="ext-token-row">
                <Typography variant="body2" sx={{ flex: 1 }}>{t.name || 'browser extension'}</Typography>
                <Typography variant="caption" color="text.secondary">{t.live ? `until ${String(t.expires_at || '').slice(0, 10)}${t.last_used_at ? ` · used ${String(t.last_used_at).slice(0, 10)}` : ''}` : (t.revoked_at ? 'disconnected' : 'expired')}</Typography>
                {t.live && <IconButton size="small" aria-label={`disconnect ${t.name}`} onClick={() => revoke(t.id)} data-testid="ext-revoke"><span aria-hidden>✕</span></IconButton>}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function defaultName(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : 'Browser';
  const os = /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  return `${browser}${os ? ` on ${os}` : ''}`;
}
